/**
 * MountedScriptsSearchSection
 *
 * Displays search matches from the mounted folder index (recursive scan),
 * so filtering works even when folders aren't expanded in the tree.
 */

import { BookOpen, FileCode2 } from 'lucide-react';
import { MountedFileContextMenu } from './MountedFileContextMenu';

export type MountedScriptMatch = {
  filePath: string;
  fileName: string;
  description?: string;
};

type MountedScriptsSearchSectionProps = {
  matches: MountedScriptMatch[];
  onOpen: (filePath: string) => void;
};

export function MountedScriptsSearchSection({
  matches,
  onOpen,
}: MountedScriptsSearchSectionProps) {
  if (matches.length === 0) return null;

  return (
    <div className="mb-1" data-testid="mounted-search-section">
      <div className="flex items-center h-7 px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <FileCode2 className="h-3.5 w-3.5" />
          <span>Mounted</span>
          <span>({matches.length})</span>
        </div>
      </div>
      <div className="py-0.5">
        {matches.map((m) => (
          <MountedFileContextMenu
            key={m.filePath}
            filePath={m.filePath}
            onOpen={() => onOpen(m.filePath)}
          >
            <button
              type="button"
              className="w-full flex items-start gap-2 px-2 py-1.5 text-left text-sm rounded-sm hover:bg-accent/50 transition-colors cursor-pointer"
              onClick={() => onOpen(m.filePath)}
              title={m.filePath}
              data-testid="mounted-search-item"
            >
              {m.filePath.toLowerCase().endsWith('.ipynb') ? (
                <BookOpen className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
              ) : (
                <FileCode2 className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
              )}
              <div className="min-w-0 flex-1">
                <span className="truncate font-medium block">{m.fileName}</span>
                {m.description && (
                  <span className="text-xs text-muted-foreground truncate block">
                    {m.description}
                  </span>
                )}
              </div>
            </button>
          </MountedFileContextMenu>
        ))}
      </div>
    </div>
  );
}

